import { useRef } from "react";
import { formatQuotedPrice } from "@shared-core/domain/formatters";
import { Button } from "../../app/components/primitives/button";
import { Card } from "../../app/components/primitives/card";
import { cn } from "../../app/components/primitives/utils";
import { ChevronUp } from "lucide-react";
import { BottomSheet } from "./BottomSheet";
import { SummaryTotals } from "./SummaryTotals";

// The money area of an order page, in the quote page's pattern (QuoteDecision.tsx), shown once and never with
// the lines again (the lines are the page's own, under its tabs):
//   - Wide (lg): a card in the page's top band (SummaryPageFrame), beside the header; the basket's alone stays
//     sticky beside its lines (`isSticky`).
//   - Narrow: a slim fixed bar (the total, what is owed, and Pay now when one tap will do it); tapping the total
//     opens a short sheet with the same content. The sheet holds no line list.
// Generic on purpose: a CIS order's page fills `children` with what is to pay and the PO number, a Tharstern-era
// order's with its invoice state (nothing is payable there). The panel words nothing about money itself.

export interface OrderSummaryProps {
  /** The panel's accessible name, e.g. "Your total and paying". */
  label: string;
  /** The sheet's title on a phone. */
  sheetTitle: string;
  /** Ex VAT, in pence. Not needed where the page supplies its own `totalsBlock` and `barTotal`. */
  totalPence?: number;
  /** Null when a price has no VAT-inclusive figure. */
  totalIncVatPence?: number | null;
  /** What the totals leave out (a delivery not yet priced), said under them. */
  totalsNote?: string | null;
  /** The page's own totals in place of the ex VAT / VAT / inc VAT block: the basket's come from the server as goods,
   *  delivery, VAT and total, not as one ex VAT figure. */
  totalsBlock?: React.ReactNode;
  /** The total the slim bar shows, when the page's figures are not the ex VAT / inc VAT pair above. */
  barTotal?: { text: string; suffix: string };
  /** Where the money stands, as the read words it: "Paid", "£495.60 to pay". Null says nothing. */
  state: { text: string; isOwed: boolean } | null;
  /** What there is to do about money (pay, give a PO number, bank details); nothing when there is none. */
  children?: React.ReactNode;
  /** The way to the price line by line; absent where the page has no such view (the basket's lines show theirs). */
  onOpenPrice?: () => void;
}

/** What both layouts share: the totals, where the money stands, then whatever there is to do. */
function SummaryContent({
  totalPence,
  totalIncVatPence,
  totalsNote,
  totalsBlock,
  state,
  children,
  onOpenPrice,
}: OrderSummaryProps) {
  return (
    <div className="space-y-4">
      {totalsBlock ?? (
        <SummaryTotals
          heading="Total"
          exVatPence={totalPence ?? null}
          incVatPence={totalIncVatPence ?? null}
          onOpenPrice={onOpenPrice}
          note={totalsNote}
        />
      )}
      {state && (
        <p
          className={cn(
            "rounded-md px-3 py-2 text-sm font-medium",
            state.isOwed
              ? "border border-warning/50 bg-warning/5 text-amber-900 dark:text-amber-200"
              : "bg-muted/60",
          )}
        >
          {state.text}
        </p>
      )}
      {children}
    </div>
  );
}

/** Wide screens: the summary card beside the header (or, with `isSticky`, beside the page's lines, staying in view
 *  and scrolling inside itself when the window is short). */
export function OrderSummaryPanel({
  panelRef,
  isSticky = false,
  ...props
}: OrderSummaryProps & {
  panelRef: React.RefObject<HTMLElement | null>;
  isSticky?: boolean;
}) {
  return (
    <aside
      ref={panelRef}
      tabIndex={-1}
      aria-label={props.label}
      className={cn(
        "focus:outline-none",
        isSticky &&
          "lg:sticky lg:top-4 lg:max-h-[calc(100dvh-2rem)] lg:self-start lg:overflow-y-auto",
      )}
    >
      <Card className="gap-4 p-5">
        <SummaryContent {...props} />
      </Card>
    </aside>
  );
}

/** Narrow screens: the slim fixed bar, and the short sheet behind it. The page owns whether the sheet is open, so
 *  a row elsewhere on the page (Needs you) can open it. */
export function OrderSummaryBar({
  isSheetOpen,
  onSheetOpenChange,
  barAction,
  ...props
}: OrderSummaryProps & {
  isSheetOpen: boolean;
  onSheetOpenChange: (isOpen: boolean) => void;
  /** The bar's own button. `onPress` set: one tap does it (Pay now when nothing else is asked); null: it opens the
   *  sheet. No `barAction`: the bar has no button (nothing to do). */
  barAction: {
    label: string;
    onPress: (() => void) | null;
    disabled?: boolean;
  } | null;
}) {
  const { totalPence, totalIncVatPence, barTotal, state } = props;
  const totalButtonRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const openSheet = (e: React.MouseEvent<HTMLElement>) => {
    openerRef.current = e.currentTarget;
    onSheetOpenChange(true);
  };
  const shownTotal = barTotal ?? {
    text: formatQuotedPrice(totalIncVatPence ?? totalPence ?? 0),
    suffix: totalIncVatPence != null ? "inc VAT" : "+ VAT",
  };

  return (
    <>
      <div
        data-testid="order-summary-bar"
        className="fixed inset-x-0 bottom-0 z-20 border-t bg-card/95 shadow-[0_-4px_12px_rgba(0,0,0,0.06)] backdrop-blur"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-2.5">
          <div className="min-w-0 flex-1">
            <button
              type="button"
              ref={totalButtonRef}
              className="flex items-center gap-1 text-left font-semibold tabular-nums"
              aria-haspopup="dialog"
              aria-expanded={isSheetOpen}
              onClick={openSheet}
            >
              {shownTotal.text}
              <span className="text-xs font-normal text-muted-foreground">
                {shownTotal.suffix}
              </span>
              <ChevronUp className="size-4 text-muted-foreground" />
            </button>
            {state && (
              <p
                className={cn(
                  "truncate text-xs",
                  state.isOwed
                    ? "font-medium text-amber-800 dark:text-amber-300"
                    : "text-muted-foreground",
                )}
              >
                {state.text}
              </p>
            )}
          </div>
          {barAction && (
            <Button
              disabled={barAction.disabled}
              onClick={(e) =>
                barAction.onPress ? barAction.onPress() : openSheet(e)
              }
            >
              {barAction.label}
            </Button>
          )}
        </div>
      </div>
      {isSheetOpen && (
        <BottomSheet
          title={props.sheetTitle}
          onClose={() => onSheetOpenChange(false)}
          returnFocusTo={openerRef.current ? openerRef : totalButtonRef}
        >
          <SummaryContent
            {...props}
            onOpenPrice={
              props.onOpenPrice &&
              (() => {
                onSheetOpenChange(false);
                props.onOpenPrice?.();
              })
            }
          />
        </BottomSheet>
      )}
    </>
  );
}
